/**
 * Portless block-digest schedule (BLU-734) — the one shape both sides share.
 *
 * The digest cron fires globally at :05/:35 (offset 5 min behind the label
 * prefetch — see services/portless-block-digest-cron.ts for why that offset is
 * load-bearing). A floor's schedule never changes WHEN the cron ticks; it only
 * decides which ticks are allowed to POST to Slack. The episode sweep runs on
 * every tick regardless, so the Issues page stays live whatever the cadence.
 *
 * Two modes, per Brett's spec (2026-07-30) — everything dropdown-constrained
 * so a schedule cannot be mistyped:
 *
 *  - `interval`: every N minutes. N is restricted to multiples of 30 that
 *    divide a day, so the existing :05/:35 ticks cover every option with
 *    stateless arithmetic (no last-sent bookkeeping).
 *  - `times`: specific 30-minute slots in a chosen IANA timezone (multiple
 *    allowed — "just once in the morning" is a single 08:00 entry). The :05
 *    tick maps to the XX:00 slot and :35 to XX:30 in that timezone, so each
 *    selected slot fires at most once per day by construction.
 *
 * Frontend renders its dropdowns from these constants; the backend validates
 * against the same ones — the two cannot drift (same pattern as
 * metafield-purposes.ts).
 */
/** Allowed "every N minutes" choices. Multiples of 30 that divide 1440. */
export const PORTLESS_DIGEST_INTERVALS = [30, 60, 90, 120, 180, 240, 360, 480, 720, 1440];
/** '00:00' … '23:30' — the 48 half-hour send slots for `times` mode. */
export const PORTLESS_DIGEST_TIME_SLOTS = Object.freeze(Array.from({ length: 48 }, (_, i) => {
    const h = String(Math.floor(i / 2)).padStart(2, '0');
    return `${h}:${i % 2 === 0 ? '00' : '30'}`;
}));
/** Hard cap on selected slots — enough for "every few hours", not a DoS. */
export const PORTLESS_DIGEST_MAX_TIMES = 8;
/** Absent/invalid config resolves to this — byte-identical to pre-BLU-734 behaviour. */
export const DEFAULT_PORTLESS_DIGEST_SCHEDULE = {
    mode: 'interval',
    intervalMinutes: 30,
};
